import { useCallback, useEffect, useState } from 'react';
import { ShoppingBag, ArrowRight, X, Truck, Pill, Sparkles } from 'lucide-react';
import { SHOP_URL } from '../../lib/constants';

export default function OnlineStorePopup({ onClose }) {
  const [closing, setClosing] = useState(false);

  const dismiss = useCallback(() => {
  setClosing(true);
  window.setTimeout(() => {
    onClose();
  }, 180);
}, [onClose]);

  // Allow closing with Escape
  /*
 useEffect(() => {
  const onKey = (e) => {
    if (e.key === 'Escape') dismiss();
  };

  window.addEventListener('keydown', onKey);

  return () => window.removeEventListener('keydown', onKey);
}, [dismiss]);

*/
useEffect(() => {
  const onKey = (e) => {
    if (e.key === 'Escape') dismiss();
  };

  window.addEventListener('keydown', onKey);

  return () => window.removeEventListener('keydown', onKey);
}, [dismiss]);

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 ${closing ? 'animate-out fade-out-0 duration-150' : 'animate-in fade-in-0 duration-300'
        }`}
      onClick={dismiss}
    >
      <div
        className={`relative w-full max-w-md bg-white rounded-[28px] shadow-2xl overflow-hidden ${closing ? 'animate-out fade-out-0 zoom-out-95 duration-150' : 'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4 duration-300'
          }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="online-store-popup-title"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/90 text-slate-500 hover:bg-white hover:text-slate-700 transition-colors flex items-center justify-center shadow-sm"
          aria-label="Close"
          data-testid="popup-close-button"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top visual banner */}
        <div className="relative h-32 bg-gradient-to-br from-cyan-500 via-blue-600 to-violet-600 overflow-hidden">
          {/* Decorative floating icons */}
          <div className="absolute -left-3 -top-3 w-16 h-16 bg-white/10 rounded-2xl rotate-12 flex items-center justify-center">
            <Pill className="w-7 h-7 text-white/70" />
          </div>
          <div className="absolute right-8 -bottom-4 w-14 h-14 bg-white/10 rounded-2xl -rotate-6 flex items-center justify-center">
            <Truck className="w-6 h-6 text-white/70" />
          </div>
          <div className="absolute right-0 top-0 w-24 h-24 bg-white/5 rounded-full -translate-y-8 translate-x-8"></div>

          {/* Main icon badge, overlapping into the content area below */}
          <div className="absolute -bottom-7 left-7 w-16 h-16 bg-white rounded-2xl shadow-lg flex items-center justify-center">
            <div className="w-11 h-11 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-5 h-5 text-white" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="pt-11 pb-7 px-7">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-600 tracking-wide uppercase mb-2">
            <Sparkles className="w-3.5 h-3.5" />
            New for Nate's customers
          </div>

          <h2
            id="online-store-popup-title"
            className="text-2xl font-bold text-slate-900 mb-2 leading-snug"
          >
            Your pharmacy, now online.
          </h2>

          <p className="text-slate-600 text-sm leading-relaxed mb-5">
            Order prescriptions, OTC essentials and more from home — free delivery still applies.
          </p>

          {/* Feature chips */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">
              <Truck className="w-3.5 h-3.5 text-green-600" />
              Free delivery
            </span>
            <span className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full">
              <Pill className="w-3.5 h-3.5 text-cyan-600" />
              OTC benefits accepted
            </span>
          </div>

          {/* Shop Button */}
          <a
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={dismiss}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3.5 rounded-full font-semibold hover:opacity-90 transition-opacity shadow-lg shadow-cyan-500/20"
            data-testid="popup-shop-online-button"
          >
            <ShoppingBag className="w-5 h-5" />
            Shop Online
            <ArrowRight className="w-4 h-4" />
          </a>

          {/* Maybe Later */}
          <button
            onClick={dismiss}
            className="w-full mt-3 py-2 text-sm text-slate-500 hover:text-slate-700 transition-colors"
            data-testid="popup-dismiss-button"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
